import { useRef, useState } from "react";
import { type AssistantState } from "../application/assistant-prototype";
import { HomeDashboardRepository } from "../application/home-dashboard";
import { type ParsedVoiceMealDraft } from "../application/remote-meal-parser";
import logo from "../assets/ui/brand/logo-horizontal.png";
import dragon from "../assets/ui/mascot/dragon.png";
import diary from "../assets/ui/nav/diary.svg";
import home from "../assets/ui/nav/home.svg";
import lists from "../assets/ui/nav/lists.svg";
import microphone from "../assets/ui/nav/microphone.svg";
import profile from "../assets/ui/nav/profile.svg";
import bell from "../assets/ui/status/notification-bell.png";
import streak from "../assets/ui/status/streak-flame.png";
import { ActivityDialog } from "../features/activity/ActivityDialog";
import { AssistantDialogs } from "../features/assistant/AssistantDialogs";
import { useAssistant } from "../features/assistant/useAssistant";
import { CalendarPicker } from "../features/calendar/CalendarPicker";
import { visibleDayMonth, visibleWeekday } from "../features/calendar/day-labels";
import { DAY_CARD_GAP, useDayNavigation } from "../features/calendar/useDayNavigation";
import { DayFoodSummary } from "../features/day-summary/DayFoodSummary";
import { useDaySummary } from "../features/day-summary/useDaySummary";
import { CalorieBalanceDialog } from "../features/home/CalorieBalanceDialog";
import { DaySlide, MacroCard, WaterCard } from "../features/home/DashboardCards";
import { meals } from "../features/home/meal-cards";
import { useDashboard } from "../features/home/useDashboard";
import { ListsScreen, type ListsScreenHandle } from "../features/lists/ListsScreen";
import { ProfileScreen } from "../features/profile/ProfileScreen";
import { StatisticsScreen } from "../features/statistics/StatisticsScreen";
import { useAutomaticReports } from "../features/statistics/useAutomaticReports";
import { DuplicateFoodDialog } from "../features/voice-input/DuplicateFoodDialog";
import { MealEntryDialogs } from "../features/voice-input/MealEntryDialogs";
import { useMealEntry } from "../features/voice-input/useMealEntry";
import { useWater } from "../features/water/useWater";
import { WaterDialog } from "../features/water/WaterDialog";
import { webMealRepository } from "../storage/indexeddb-meal-repository";
import { webWaterRepository } from "../storage/indexeddb-water-repository";
import { ListsRepository } from "../storage/local-lists-repository";
import { webSpeechTranscription } from "../voice/web-speech-transcription";
import { NavItem, SectionHeading } from "./components/AppNavigation";
import { SwipeDismissDialog } from "./components/SwipeDismissDialog";
const navigation = [
  { label: "Домой", image: home },
  { label: "Дневник", image: diary },
  { label: "Списки", image: lists },
  { label: "Профиль", image: profile },
] as const;

const dashboardRepository = new HomeDashboardRepository(webWaterRepository, webMealRepository);
const listsRepository = new ListsRepository(localStorage);

export function App({ previewDraft = null, previewAssistantState = null }: { previewDraft?: ParsedVoiceMealDraft | null; previewAssistantState?: AssistantState | null }) {
  useAutomaticReports(dashboardRepository, webMealRepository);
  const homeDashboard = useDashboard(dashboardRepository);
  const { currentDate, selectedDate, setSelectedDate, dashboard, previousDashboard, nextDashboard, dashboardStatus, setDashboardStatus, refreshDashboard } = homeDashboard;
  const { daySlideOffset, daySlidePhase, animateTodayRing, changeDay, startDaySwipe, moveDaySwipe, finishDaySwipe, finishDaySlide, cancelDaySwipe, suppressDayClick } = useDayNavigation(homeDashboard);
  const summary = useDaySummary(webMealRepository, selectedDate);
  const { daySummaryOpen, setDaySummaryOpen, daySummaryDrafts, daySummaryLoading } = summary;
  const mealEntry = useMealEntry({ selectedDate, repository: webMealRepository, voiceProvider: webSpeechTranscription, previewDraft,
    onNavigate: setSelectedDate, onCloseDaySummary: () => setDaySummaryOpen(false), onLoadError: () => setDashboardStatus("error") });
  const { debugDraft, emptyMealType, transferNotice, presentParsedDraft, openSavedMeal, handleMealMicrophone, leaveMealContext } = mealEntry;
  const assistant = useAssistant({ previewAssistantState, voiceProvider: webSpeechTranscription, presentParsedDraft });
  const { assistantState, setAssistantInfoOpen, openAssistant } = assistant;
  const water = useWater({ selectedDate, dashboard, dashboardRepository });
  const { glassMilliliters, addWater, openWater } = water;
  const [activeTab, setActiveTab] = useState("Домой");
  const listsScreen = useRef<ListsScreenHandle>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [activityDate, setActivityDate] = useState<string | null>(null);
  const backSwipe = useRef<{ x: number; y: number } | null>(null);
  const startBackSwipe = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === "mouse" || event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (target.closest("button,a,input,textarea,select,[contenteditable='true'],[role='button'],.swipe-delete-row,.nutrition-swipe-row")) { backSwipe.current = null; return; }
    backSwipe.current = { x: event.clientX, y: event.clientY };
  };
  const finishBackSwipe = (event: React.PointerEvent<HTMLElement>) => {
    const start = backSwipe.current; backSwipe.current = null;
    if (!start || event.clientX - start.x < 80 || Math.abs(event.clientY - start.y) > 70) return;
    if (document.querySelector("[role='dialog'],.popup-backdrop,.voice-backdrop,.calendar-backdrop")) return;
    if (activeTab !== "Домой") setActiveTab("Домой");
  };
  const handleGlobalMicrophone = () => {
    if (activeTab === "Списки") listsScreen.current?.microphone();
    else handleMealMicrophone();
  };
  const openDaySummary = () => { leaveMealContext(); return summary.openDaySummary(); };
  return (
    <main className="app" aria-label="Главный экран YumNote" onPointerDown={startBackSwipe} onPointerUp={finishBackSwipe} onPointerCancel={() => { backSwipe.current = null; }}><DuplicateFoodDialog />
      {!debugDraft && !emptyMealType && <><div className="dashboard-content">
      <header className="topbar">
        <img className="brand" src={logo} alt="YumNote" />
        <button className="assistant-top-button assistant-start" type="button" aria-label="Открыть разговор с ЯмНямом" onClick={openAssistant}>S</button>
        <button className="assistant-top-button assistant-info" type="button" aria-label="Инспектор памяти" onClick={() => setAssistantInfoOpen(true)}>I</button>
        <button className="icon-button notification" type="button" aria-label="Уведомления"><img src={bell} alt="" /></button>
      </header>

      {activeTab === "Списки" ? <ListsScreen ref={listsScreen} repository={listsRepository} voiceProvider={webSpeechTranscription} onHome={() => setActiveTab("Домой")} /> : activeTab === "Профиль" ? <ProfileScreen assistant={assistantState} onEditGoal={openAssistant} renderStatistics={onBack => <StatisticsScreen dashboards={dashboardRepository} meals={webMealRepository} onBack={onBack} />} /> : <>

      <section className="streak-card" aria-label="Дата и серия дней">
        <div className="day-switcher">
          <button type="button" aria-label="Предыдущий день" onClick={() => changeDay(-1)}>‹</button>
          <button className="day-calendar" type="button" aria-label="Открыть календарь" onClick={() => setCalendarOpen(true)}><span className="day-date-copy" key={selectedDate}><small>{visibleWeekday(selectedDate)}</small><b id="today-title">{visibleDayMonth(selectedDate, currentDate)}</b></span></button>
          <button type="button" aria-label="Следующий день" disabled={selectedDate >= currentDate} onClick={() => changeDay(1)}>›</button>
        </div>
        <img className="streak-icon" src={streak} alt={`Серия: ${dashboard.streakDays} дней`} />
      </section>
      {transferNotice?.date === selectedDate && <p className="meal-transfer-notice" role="status">{transferNotice.text}</p>}

      <section className="today" aria-labelledby="today-title" onPointerDown={startDaySwipe} onPointerMove={moveDaySwipe} onPointerUp={finishDaySwipe} onPointerCancel={cancelDaySwipe} onClickCapture={suppressDayClick}>

        <div className="day-slide-viewport">
          <div className={`day-slide-layer${daySlidePhase === "dragging" ? " is-dragging" : ""}`} style={{ transform: `translateX(calc(-100cqw - ${DAY_CARD_GAP}px + ${daySlideOffset}px))` }} onTransitionEnd={finishDaySlide}>
            <DaySlide dashboard={previousDashboard} />
            <DaySlide dashboard={dashboard} onOpen={() => void openDaySummary()} onActivity={() => setActivityDate(selectedDate)} onBalance={() => setBalanceOpen(true)} animateRing={animateTodayRing && selectedDate === currentDate} />
            <DaySlide dashboard={nextDashboard ?? dashboard} />
          </div>
        </div>
        <img className="dragon" src={dragon} alt="Дракончик YumNote с чашкой" />
      </section>

      <div className="lower-stack">
      <div className="day-secondary-viewport" aria-label="Макроэлементы" onPointerDown={startDaySwipe} onPointerMove={moveDaySwipe} onPointerUp={finishDaySwipe} onPointerCancel={cancelDaySwipe} onClickCapture={suppressDayClick}>
        <div className={`day-secondary-layer${daySlidePhase === "dragging" ? " is-dragging" : ""}`} style={{ transform: `translateX(calc(-100cqw - ${DAY_CARD_GAP}px + ${daySlideOffset}px))` }}>
          <MacroCard dashboard={previousDashboard} />
          <MacroCard dashboard={dashboard} />
          <MacroCard dashboard={nextDashboard ?? dashboard} />
        </div>
      </div>

      <div className="day-secondary-viewport" aria-label="Вода" onPointerDown={startDaySwipe} onPointerMove={moveDaySwipe} onPointerUp={finishDaySwipe} onPointerCancel={cancelDaySwipe} onClickCapture={suppressDayClick}>
        <div className={`day-secondary-layer${daySlidePhase === "dragging" ? " is-dragging" : ""}`} style={{ transform: `translateX(calc(-100cqw - ${DAY_CARD_GAP}px + ${daySlideOffset}px))` }}>
          <WaterCard dashboard={previousDashboard} glassMilliliters={glassMilliliters} />
          <WaterCard dashboard={dashboard} glassMilliliters={glassMilliliters} status={water.waterStatus === "idle" ? dashboardStatus : water.waterStatus} onAdd={() => void addWater()} onOpen={openWater} />
          <WaterCard dashboard={nextDashboard ?? dashboard} glassMilliliters={glassMilliliters} />
        </div>
      </div>

      <section className="meals" aria-labelledby="meals-title">
        <SectionHeading id="meals-title" title="Приёмы пищи" />
        <div className="meal-grid">{meals.map(meal => {
          const itemCount = dashboard.meals[meal.type].itemCount;
          return <button className="meal" type="button" key={meal.type} aria-label={`${meal.label}, ${itemCount} позиций, ${itemCount > 0 ? "заполнено" : "пока пусто"}`} onClick={() => void openSavedMeal(meal.type)}>
            <img src={meal.image} alt="" /><span>{meal.label}</span>
            {itemCount > 0 && <i className="meal-filled-mark" aria-hidden="true" />}
          </button>;
        })}</div>
      </section>
      </div>
      </>}
      </div>

      <nav className="bottom-nav" aria-label="Основная навигация">
        <NavItem item={navigation[0]} active={activeTab === navigation[0].label} onClick={setActiveTab} />
        <NavItem item={navigation[1]} active={activeTab === navigation[1].label} onClick={setActiveTab} />
        <button className="mic-button" type="button" aria-label="Начать голосовой ввод" onClick={handleGlobalMicrophone}><img src={microphone} alt="" /></button>
        <NavItem item={navigation[2]} active={activeTab === navigation[2].label} onClick={setActiveTab} />
        <NavItem item={navigation[3]} active={activeTab === navigation[3].label} onClick={setActiveTab} />
      </nav></>}

      <MealEntryDialogs daySummaryOpen={daySummaryOpen} controller={mealEntry} selectedDate={selectedDate} currentDate={currentDate} />

      <AssistantDialogs controller={assistant} refreshDashboard={refreshDashboard} />

      {calendarOpen && <div className="calendar-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) setCalendarOpen(false); }}>
        <SwipeDismissDialog className="calendar-modal" role="dialog" aria-modal="true" aria-label="Выбор даты" onDismiss={() => setCalendarOpen(false)}>
          <CalendarPicker selected={selectedDate} current={currentDate} onSelect={date => { setSelectedDate(date); setCalendarOpen(false); }} />
        </SwipeDismissDialog>
      </div>}

      {daySummaryOpen && <div className="debug-backdrop popup-backdrop popup-backdrop--voice" role="presentation">
        <SwipeDismissDialog className="debug-modal day-summary-modal" role="dialog" aria-modal="true" aria-labelledby="day-summary-title" onDismiss={() => setDaySummaryOpen(false)}>
          <button className="voice-close" type="button" aria-label="Закрыть" onClick={() => setDaySummaryOpen(false)}>×</button>
          <h2 id="day-summary-title">Съедено за день</h2>
          <DayFoodSummary dashboard={dashboard} drafts={daySummaryDrafts} loading={daySummaryLoading} />
          <button className="voice-primary debug-done" type="button" onClick={() => setDaySummaryOpen(false)}>Готово</button>
        </SwipeDismissDialog>
      </div>}

      {balanceOpen && <CalorieBalanceDialog dashboard={dashboard} onClose={() => setBalanceOpen(false)} />}
      {activityDate && <ActivityDialog initialDate={activityDate} weightKg={typeof assistantState.profile.weightKg === "number" ? assistantState.profile.weightKg : null} onClose={() => setActivityDate(null)} onSaved={() => { setActivityDate(null); void refreshDashboard(); }} />}

      <WaterDialog controller={water} />

    </main>
  );
}
